import { SIGNATURE_EXEMPT_METHODS, isSignatureRequired } from '@platform/shared-types';

/**
 * Per-payment-type "Require customer signature" rules (spec Part 2).
 * Runtime shared-types values are read defensively: a stale Vite pre-bundle
 * returns `undefined` at module scope and would white-screen the terminal page.
 * Degrade to the contract values (house pattern: pages/legal/documentCategories.ts).
 */
const CONTRACT_EXEMPT = ['split'] as const;
const STALE_BUNDLE_HINT =
  'a stale Vite pre-bundle. RESTART the dev server: deleting node_modules/.vite under a running server does nothing, because Vite keeps the optimised bundle in memory.';

const EXEMPT: readonly string[] = (() => {
  if (Array.isArray(SIGNATURE_EXEMPT_METHODS)) return SIGNATURE_EXEMPT_METHODS as readonly string[];
  console.error(`@platform/shared-types exported no SIGNATURE_EXEMPT_METHODS — ${STALE_BUNDLE_HINT}`);
  return CONTRACT_EXEMPT;
})();

const sharedIsRequired: ((map: Record<string, boolean> | undefined, method: string) => boolean) | null = (() => {
  if (typeof isSignatureRequired === 'function') return isSignatureRequired;
  console.error(`@platform/shared-types exported no isSignatureRequired — ${STALE_BUNDLE_HINT}`);
  return null;
})();

export function isSignatureEligible(methodId: string): boolean {
  return !EXEMPT.includes(methodId);
}

export function normalizeSignatureMap(raw: unknown): Record<string, boolean> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === 'boolean') out[k] = v;
  }
  return out;
}

export function readSignatureRequired(map: Record<string, boolean> | undefined, methodId: string): boolean {
  if (!isSignatureEligible(methodId)) return false;
  if (sharedIsRequired) return sharedIsRequired(map, methodId);
  return map?.[methodId] === true;
}

/** Explicit false when turning off — ext3 merges per key, so a dropped key would keep the old true. */
export function toggleSignatureRequired(map: Record<string, boolean>, methodId: string): Record<string, boolean> {
  if (!isSignatureEligible(methodId)) return map;
  return { ...map, [methodId]: !readSignatureRequired(map, methodId) };
}

export function signatureMapsEqual(a: Record<string, boolean>, b: Record<string, boolean>): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) {
    if (readSignatureRequired(a, k) !== readSignatureRequired(b, k)) return false;
  }
  return true;
}
